/** Entraînement IA : session de questions générées à la volée. */
import type { CertQuestion, CertQuestionAnswer } from '@unity-learning/shared';
import { apiGet, apiPost } from './client';

/** Session ouverte ; `B` est la forme d'un élément du plan de questions (définie par la page). */
export interface AiTrainingSession<B = unknown> {
  sessionId: string;
  blueprint: B[];
  durationMinutes: number;
}

export function startAiTraining<B = unknown>(params: { questionCount: number; durationMinutes: number }): Promise<AiTrainingSession<B>> {
  return apiPost<AiTrainingSession<B>>('/api/ai-training/start', params);
}

/** Question n°`index` de la session (générée au premier appel : 1 appel Gemini). */
export function getAiTrainingQuestion(
  sessionId: string,
  index: number,
  options: { signal?: AbortSignal } = {}
): Promise<CertQuestion> {
  return apiGet<{ question: CertQuestion }>(
    `/api/ai-training/question/${encodeURIComponent(sessionId)}/${index}`,
    options
  ).then((data) => data.question);
}

/** Session en cours, pour la reprendre après un F5 : plan, réponses enregistrées, questions déjà générées. */
export interface AiTrainingSessionSnapshot<B = unknown> extends AiTrainingSession<B> {
  questionCount: number;
  startedAt: string;
  answers: Record<string, CertQuestionAnswer>;
  questions: { index: number; question: CertQuestion }[];
}

/** 404 si le serveur a redémarré (sessions en mémoire), 403 si la session est à quelqu'un d'autre. */
export function getAiTrainingSession<B = unknown>(sessionId: string): Promise<AiTrainingSessionSnapshot<B>> {
  return apiGet<AiTrainingSessionSnapshot<B>>(`/api/ai-training/session/${encodeURIComponent(sessionId)}`);
}

export function saveAiTrainingAnswer(sessionId: string, answer: CertQuestionAnswer): Promise<void> {
  return apiPost<void>(`/api/ai-training/answer/${encodeURIComponent(sessionId)}`, answer);
}

export function submitAiTraining(
  sessionId: string,
  submissionType: 'manual' | 'timeout'
): Promise<{ attemptId: string }> {
  return apiPost<{ attemptId: string }>(`/api/ai-training/submit/${encodeURIComponent(sessionId)}`, { submissionType });
}
